// Shared text-alignment matcher for highlighting a cited chunk inside rendered
// document text. Used by both the DOCX and PDF preview modals so the matching
// behaviour can't drift between them.
//
// Why this isn't a plain substring search:
//   * The chunk text is lossy — an LLM summary, a blank line, then the verbatim
//     source with paragraph/heading boundaries glued together with no separator
//     ("App.What", "mean?According"). Renderers, by contrast, separate those
//     blocks with whitespace. So we match in *whitespace-stripped* (compact)
//     space, which reconciles the two.
//   * The same wording frequently repeats elsewhere in the document (intros
//     reprinted in tweets/emails, boilerplate, etc.). Matching each short phrase
//     independently would land on the first textual duplicate. So we use the
//     phrases' ordering — each match votes for an anchor (its position minus its
//     offset within the chunk) and the densest aligned cluster wins.
//
// Given the rendered document's flat text, returns merged [{start, end}] ranges
// (in flatText coordinates) covering the chunk's single true location, or [] if
// nothing aligns (callers then fall back to no highlight).

const QUOTE_MAP = { "‘": "'", "’": "'", "“": '"', "”": '"' };
const norm = (s) => s.replace(/[A-Z]/g, (c) => c.toLowerCase()).replace(/[‘’“”]/g, (c) => QUOTE_MAP[c]);

export function findChunkRanges(flatText, chunkContent) {
  if (!flatText || !chunkContent) return [];

  // Verbatim source = everything after the summary paragraph (split on a blank
  // line). When there's no blank line, treat the whole thing as source.
  const parts = chunkContent.split(/\n{2,}/);
  const sourceText = parts.length > 1 ? parts.slice(1).join(" ") : chunkContent;

  // 10-word windows, stride 5, each tagged with its offset in the compact
  // (whitespace-free) chunk. The cap is a safety ceiling for a pathologically
  // large chunk, not a coverage limit — real chunks run a few hundred words.
  const WINDOW = 10;
  const STRIDE = 5;
  const PHRASES_MAX = 1000;
  const wordRe = /\S+/g;
  const words = [];
  let wm;
  let compactLen = 0;
  while ((wm = wordRe.exec(sourceText))) {
    words.push({ text: wm[0], coff: compactLen });
    compactLen += wm[0].length;
  }
  if (words.length === 0) return [];
  const phrases = [];
  for (let i = 0; i + WINDOW <= words.length && phrases.length < PHRASES_MAX; i += STRIDE) {
    const slice = words.slice(i, i + WINDOW);
    phrases.push({ text: slice.map((w) => w.text).join(""), coff: slice[0].coff });
  }
  if (phrases.length === 0) phrases.push({ text: words.map((w) => w.text).join(""), coff: 0 });

  // Project the rendered text into the same compact space (drop all whitespace)
  // and keep a map from each compact index back to its position in flatText.
  let compactFlat = "";
  const compactToFlat = [];
  for (let i = 0; i < flatText.length; i += 1) {
    if (/\s/.test(flatText[i])) continue;
    compactToFlat.push(i);
    compactFlat += flatText[i];
  }
  compactFlat = norm(compactFlat);
  if (compactFlat.length === 0) return [];

  // Every occurrence of every phrase, tagged with delta (≈ chunk start position)
  // and the phrase index. Two caps keep this bounded on pathologically
  // repetitive documents — where a 10-word phrase can recur hundreds of times —
  // so the O(N²) alignment vote below can't blow up and freeze the UI thread.
  // On normal prose a phrase matches about once, so neither cap is reached and
  // behaviour is unchanged:
  //   * MATCHES_PER_PHRASE stops one boilerplate phrase from crowding the others
  //     out of the (capped) vote and starving it of diverse anchors.
  //   * MAX_OCCURRENCES is a hard ceiling on the vote's input size.
  const MATCHES_PER_PHRASE = 16;
  const MAX_OCCURRENCES = 2500;
  const occurrences = [];
  for (let pi = 0; pi < phrases.length; pi += 1) {
    const needle = norm(phrases[pi].text);
    const coff = phrases[pi].coff;
    let from = 0;
    let idx;
    let hits = 0;
    while ((idx = compactFlat.indexOf(needle, from)) !== -1) {
      occurrences.push({ cStart: idx, cEnd: idx + needle.length, delta: idx - coff, pi });
      from = idx + needle.length;
      hits += 1;
      if (hits >= MATCHES_PER_PHRASE) break;
    }
    if (occurrences.length >= MAX_OCCURRENCES) break;
  }
  if (occurrences.length === 0) return [];

  // Alignment vote: the delta backed by the most *distinct* phrases within a
  // tight band, then snap the anchor to the median of that winner's supporting
  // deltas (the cluster centre) so a fringe duplicate grazing the band can't
  // pull the anchor off the true passage.
  const BAND = 120;
  // A real chunk start is always >= 0, so a negative delta can only come from a
  // spurious phrase match — exclude it from the vote (and the smaller-delta
  // tie-breaker below) so it can't anchor to an invalid start position.
  let bestDelta = occurrences.find((o) => o.delta >= 0)?.delta ?? occurrences[0].delta;
  let bestSupport = -1;
  for (const cand of occurrences) {
    if (cand.delta < 0) continue;
    const phraseIds = new Set();
    for (const o of occurrences) {
      if (o.delta < 0) continue;
      if (Math.abs(o.delta - cand.delta) <= BAND) phraseIds.add(o.pi);
    }
    if (phraseIds.size > bestSupport || (phraseIds.size === bestSupport && cand.delta < bestDelta)) {
      bestSupport = phraseIds.size;
      bestDelta = cand.delta;
    }
  }
  const supportDeltas = occurrences
    .filter((o) => o.delta >= 0 && Math.abs(o.delta - bestDelta) <= BAND)
    .map((o) => o.delta)
    .sort((a, b) => a - b);
  const anchor = supportDeltas[Math.floor(supportDeltas.length / 2)];

  // Confine to the span the anchor implies, mapping each surviving occurrence
  // back to flat coordinates.
  const spanLo = anchor - 64;
  const spanHi = anchor + compactLen + 256;
  const ranges = [];
  for (const o of occurrences) {
    if (o.cStart < spanLo || o.cStart > spanHi) continue;
    ranges.push({ start: compactToFlat[o.cStart], end: compactToFlat[o.cEnd - 1] + 1 });
  }
  if (ranges.length === 0) return [];

  // Merge overlapping/adjacent ranges so callers don't double-wrap.
  ranges.sort((a, b) => a.start - b.start);
  const merged = [];
  for (const r of ranges) {
    const last = merged[merged.length - 1];
    if (last && r.start <= last.end) last.end = Math.max(last.end, r.end);
    else merged.push({ ...r });
  }
  return merged;
}
